import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Search, Workflow, Eye, User, Mail, Phone, MapPin, FileText, 
  ExternalLink, ChevronRight, Calendar, Briefcase, RefreshCw,
  UserPlus, Star, Filter, Clock, Sparkles, Zap, AlertCircle, FileSearch,
  MessageSquare, Send
} from 'lucide-react';
import { toast } from 'sonner';
import { CommunicationHistory } from '../../components/CommunicationHistory';
import { SendEmailDialog, ScreeningNoteDialog } from '../../components/SendEmailDialog';
import { InterviewScheduleDialog } from '../../components/InterviewScheduleDialog';

const API = '/api';

const PIPELINE_STAGES = [
  { key: 'new', label: 'New', color: 'bg-blue-100 text-blue-800', dotColor: 'bg-blue-500' },
  { key: 'screening', label: 'Screening', color: 'bg-yellow-100 text-yellow-800', dotColor: 'bg-yellow-500' },
  { key: 'shortlisted', label: 'Shortlisted', color: 'bg-purple-100 text-purple-800', dotColor: 'bg-purple-500' },
  { key: 'interview_scheduled', label: 'Interview Scheduled', color: 'bg-indigo-100 text-indigo-800', dotColor: 'bg-indigo-500' },
  { key: 'interview_confirmed', label: 'Interview Confirmed', color: 'bg-emerald-100 text-emerald-800', dotColor: 'bg-emerald-500' },
  { key: 'reschedule_requested', label: 'Reschedule Requested', color: 'bg-amber-100 text-amber-800', dotColor: 'bg-amber-500' },
  { key: 'interview_1', label: 'Interview 1', color: 'bg-orange-100 text-orange-800', dotColor: 'bg-orange-500' },
  { key: 'interview_2', label: 'Interview 2', color: 'bg-pink-100 text-pink-800', dotColor: 'bg-pink-500' },
  { key: 'offer', label: 'Offer', color: 'bg-cyan-100 text-cyan-800', dotColor: 'bg-cyan-500' },
  { key: 'hired', label: 'Hired', color: 'bg-green-100 text-green-800', dotColor: 'bg-green-500' },
  { key: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-800', dotColor: 'bg-red-500' },
  { key: 'withdrawn', label: 'Withdrawn', color: 'bg-gray-100 text-gray-800', dotColor: 'bg-gray-500' },
];

export default function AdminApplicationsPage() {
  const { language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [applications, setApplications] = useState([]);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState(searchParams.get('stage') || '');
  const [stageStats, setStageStats] = useState({});
  
  // Dialog state
  const [selectedApp, setSelectedApp] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [updatingStage, setUpdatingStage] = useState(false);
  const [newNotes, setNewNotes] = useState('');
  const [runningAIScreening, setRunningAIScreening] = useState(false);
  const [rerunningScreening, setRerunningScreening] = useState(false);
  const [cvPreview, setCvPreview] = useState(null);
  const [loadingCvPreview, setLoadingCvPreview] = useState(false);
  const [showCvPreview, setShowCvPreview] = useState(false);
  
  // Communication dialog states
  const [sendEmailDialogOpen, setSendEmailDialogOpen] = useState(false);
  const [screeningNoteDialogOpen, setScreeningNoteDialogOpen] = useState(false);
  const [interviewScheduleDialogOpen, setInterviewScheduleDialogOpen] = useState(false);

  const jobId = searchParams.get('job_id');

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      let url = jobId 
        ? `${API}/admin/ats/jobs/${jobId}/applications`
        : `${API}/admin/ats/applications`;
      
      // Apply stage filter to the API call
      const params = new URLSearchParams();
      if (stageFilter && stageFilter !== 'all') {
        params.append('stage', stageFilter);
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setApplications(data.applications || []);
        if (data.job) setJob(data.job);
        if (data.by_stage) setStageStats(data.by_stage);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [jobId, stageFilter]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleViewApplication = async (app) => {
    try {
      const response = await fetch(`${API}/admin/ats/applications/${app.application_id}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSelectedApp(data);
        setNewNotes(data.recruiter_notes || '');
        setDetailDialogOpen(true);
      }
    } catch (error) {
      toast.error('Failed to load application details');
    }
  };

  const handleUpdateStage = async (newStage) => {
    if (!selectedApp) return;
    
    setUpdatingStage(true);
    try {
      const response = await fetch(`${API}/admin/ats/applications/${selectedApp.application_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pipeline_stage: newStage })
      });
      
      if (response.ok) {
        toast.success(`Moved to ${newStage.replace(/_/g, ' ')}`);
        setSelectedApp(prev => ({ ...prev, pipeline_stage: newStage }));
        fetchApplications();
      } else {
        toast.error('Failed to update stage');
      }
    } catch (error) {
      toast.error('Failed to update stage');
    } finally {
      setUpdatingStage(false);
    }
  };

  const handleUpdateNotes = async () => {
    if (!selectedApp) return;
    
    try {
      const response = await fetch(`${API}/admin/ats/applications/${selectedApp.application_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ recruiter_notes: newNotes })
      });
      
      if (response.ok) {
        toast.success('Notes saved');
        setSelectedApp(prev => ({ ...prev, recruiter_notes: newNotes }));
      } else {
        toast.error('Failed to save notes');
      }
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  const handleRunAIScreening = async () => {
    if (!selectedApp) return;
    
    setRunningAIScreening(true);
    try {
      const response = await fetch(`${API}/admin/ats/applications/${selectedApp.application_id}/screen`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.screening_result?.screening_status === 'completed') {
          toast.success('AI screening completed');
          setSelectedApp(prev => ({ 
            ...prev, 
            ai_screening: data.screening_result,
            ai_screened_at: data.screening_result?.screened_at
          }));
          fetchApplications(); // Refresh list
        } else {
          toast.error(data.screening_result?.recommendation || 'AI screening failed');
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to run AI screening');
      }
    } catch (error) {
      toast.error('Failed to run AI screening');
    } finally {
      setRunningAIScreening(false);
    }
  };

  const handleRerunScreening = async (forceReparse = false) => {
    if (!selectedApp?.candidate?.candidate_id && !selectedApp?.candidate_id) {
      toast.error('No candidate linked to this application');
      return;
    }
    
    setRerunningScreening(true);
    try {
      const candidateId = selectedApp?.candidate?.candidate_id || selectedApp?.candidate_id;
      
      // If forceReparse, parse CV first
      if (forceReparse && candidateId) {
        const parseResponse = await fetch(`${API}/admin/candidates/${candidateId}/parse-cv`, {
          method: 'POST',
          credentials: 'include'
        });
        if (parseResponse.ok) {
          toast.success('CV parsed successfully');
        }
      }
      
      // Re-run screening
      const response = await fetch(`${API}/admin/ats/applications/${selectedApp.application_id}/rescreen?force_reparse=${forceReparse}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.screening_result?.screening_status === 'completed') {
          toast.success('AI screening completed');
          setSelectedApp(prev => ({ 
            ...prev, 
            ai_screening: data.screening_result,
            ai_screened_at: data.screening_result?.screened_at
          }));
          fetchApplications();
        } else {
          toast.error(data.screening_result?.recommendation || 'AI screening failed');
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to re-run screening');
      }
    } catch (error) {
      toast.error('Failed to re-run screening');
    } finally {
      setRerunningScreening(false);
    }
  };

  const handleLoadCvPreview = async () => {
    const candidateId = selectedApp?.candidate?.candidate_id || selectedApp?.candidate_id;
    if (!candidateId) {
      toast.error('No candidate linked');
      return;
    }
    
    setLoadingCvPreview(true);
    try {
      const response = await fetch(`${API}/admin/candidates/${candidateId}/cv-preview`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setCvPreview(data);
        setShowCvPreview(true);
      } else {
        toast.error('Failed to load CV preview');
      }
    } catch (error) {
      toast.error('Failed to load CV preview');
    } finally {
      setLoadingCvPreview(false);
    }
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = 
      app.candidate?.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      app.candidate?.email?.toLowerCase().includes(search.toLowerCase()) ||
      app.job?.title?.toLowerCase().includes(search.toLowerCase());
    
    // Apply stage filter for all views (both job-specific and all-applications)
    const matchesStage = !stageFilter || stageFilter === 'all' || app.pipeline_stage === stageFilter;
    
    return matchesSearch && matchesStage;
  });

  const getStageBadge = (stage) => {
    const stageInfo = PIPELINE_STAGES.find(s => s.key === stage);
    return stageInfo 
      ? <Badge className={stageInfo.color}>{stageInfo.label}</Badge>
      : <Badge>{stage}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="p-6" data-testid="admin-applications-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111827] flex items-center gap-2">
            <Workflow className="h-6 w-6 text-[#33AADD]" />
            {job ? `Applications: ${job.title}` : 'All Applications'}
          </h1>
          <p className="text-[#6B7280]">
            {job ? `${job.location || 'Remote'} • ${applications.length} applicants` : 'Manage candidate applications pipeline'}
          </p>
        </div>
        <Button variant="outline" onClick={fetchApplications}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stage Stats - Only for job view */}
      {jobId && Object.keys(stageStats).length > 0 && (
        <div className="grid grid-cols-4 md:grid-cols-9 gap-2 mb-6">
          {PIPELINE_STAGES.map((stage) => {
            const count = stageStats[stage.key] || 0;
            const isActive = stageFilter === stage.key;
            return (
              <button
                key={stage.key}
                onClick={() => setStageFilter(isActive ? '' : stage.key)}
                className={`p-2 rounded-lg border text-center transition-all ${
                  isActive ? 'border-[#33AADD] bg-[#33AADD]/5' : 'border-[#E5E7EB] hover:border-[#33AADD]/50'
                }`}
              >
                <span className={`inline-block h-2 w-2 rounded-full ${stage.dotColor} mb-1`}></span>
                <p className="text-xs text-[#6B7280]">{stage.label}</p>
                <p className="text-lg font-bold text-[#111827]">{count}</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Search & Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
              <Input
                placeholder="Search by name, email, or job..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            {!jobId && (
              <Select value={stageFilter} onValueChange={setStageFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Stages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stages</SelectItem>
                  {PIPELINE_STAGES.map(s => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Applications Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#33AADD]"></div>
            </div>
          ) : filteredApplications.length === 0 ? (
            <div className="text-center py-12 text-[#6B7280]">
              <Workflow className="h-12 w-12 mx-auto mb-4 text-[#E5E7EB]" />
              <p>No applications found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F9FAFB]">
                    <TableHead>Candidate</TableHead>
                    {!jobId && <TableHead>Job</TableHead>}
                    <TableHead>Location</TableHead>
                    <TableHead>Experience</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Applied</TableHead>
                    <TableHead>AI Fit</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredApplications.map((app) => (
                    <TableRow key={app.application_id} data-testid={`app-row-${app.application_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-[#33AADD]/10 flex items-center justify-center text-[#33AADD] font-medium text-sm">
                            {app.candidate?.full_name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-[#111827]">{app.candidate?.full_name || 'Unknown'}</p>
                            <p className="text-xs text-[#6B7280]">{app.candidate?.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      {!jobId && (
                        <TableCell>
                          <p className="text-sm text-[#111827]">{app.job?.title || 'Unknown'}</p>
                          <p className="text-xs text-[#6B7280]">{app.job?.location}</p>
                        </TableCell>
                      )}
                      <TableCell className="text-[#6B7280] text-sm">
                        {app.candidate?.current_location || '-'}
                      </TableCell>
                      <TableCell className="text-[#6B7280] text-sm">
                        {app.candidate?.years_of_experience ? `${app.candidate.years_of_experience} yrs` : '-'}
                      </TableCell>
                      <TableCell>{getStageBadge(app.pipeline_stage)}</TableCell>
                      <TableCell className="text-[#6B7280] text-sm">{formatDate(app.applied_at)}</TableCell>
                      <TableCell>
                        {app.ai_screening?.screening_status === 'completed' ? (
                          <Badge className={
                            app.ai_screening.fit_level === 'excellent' ? 'bg-green-100 text-green-800' :
                            app.ai_screening.fit_level === 'good' ? 'bg-blue-100 text-blue-800' :
                            app.ai_screening.fit_level === 'fair' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }>
                            {app.ai_screening.fit_score}%
                          </Badge>
                        ) : app.ai_screening?.screening_status === 'error' ? (
                          <Badge variant="outline" className="text-amber-500">
                            <AlertCircle className="h-3 w-3 mr-1" /> Error
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[#9CA3AF]">Pending</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleViewApplication(app)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Application Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Application Details</DialogTitle>
          </DialogHeader>
          
          {selectedApp && (
            <div className="space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Candidate Info */}
              <div className="flex items-start gap-4 p-4 bg-[#F9FAFB] rounded-lg">
                <div className="h-14 w-14 rounded-full bg-[#33AADD]/10 flex items-center justify-center text-[#33AADD] text-xl font-medium">
                  {selectedApp.candidate?.full_name?.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-[#111827]">{selectedApp.candidate?.full_name}</h3>
                  <p className="text-[#6B7280]">{selectedApp.candidate?.functional_specialization || 'Candidate'}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {getStageBadge(selectedApp.pipeline_stage)}
                    {selectedApp.candidate?.years_of_experience && (
                      <Badge variant="secondary">{selectedApp.candidate.years_of_experience} years exp</Badge>
                    )}
                  </div>
                </div>
                {selectedApp.candidate?.cv_url && (
                  <a href={selectedApp.candidate.cv_url} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm">
                      <FileText className="h-4 w-4 mr-1" /> View CV
                    </Button>
                  </a>
                )}
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2 text-[#6B7280]">
                  <Mail className="h-4 w-4" />
                  <a href={`mailto:${selectedApp.candidate?.email}`} className="text-[#33AADD] hover:underline">
                    {selectedApp.candidate?.email}
                  </a>
                </div>
                {selectedApp.candidate?.current_location && (
                  <div className="flex items-center gap-2 text-[#6B7280]">
                    <MapPin className="h-4 w-4" />
                    {selectedApp.candidate.current_location}
                  </div>
                )}
                {selectedApp.candidate?.linkedin_url && (
                  <div className="flex items-center gap-2 text-[#6B7280]">
                    <ExternalLink className="h-4 w-4" />
                    <a href={selectedApp.candidate.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[#33AADD] hover:underline">
                      LinkedIn Profile
                    </a>
                  </div>
                )}
                <div className="flex items-center gap-2 text-[#6B7280]">
                  <Calendar className="h-4 w-4" />
                  Applied {formatDate(selectedApp.applied_at)}
                </div>
              </div>

              {/* Job Info */}
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-blue-600" />
                  <span className="font-medium text-blue-900">{selectedApp.job?.title}</span>
                </div>
                <p className="text-sm text-blue-700 mt-1">{selectedApp.job?.location}</p>
              </div>

              {/* Skills */}
              {selectedApp.candidate?.skills?.length > 0 && (
                <div>
                  <Label className="text-[#6B7280] mb-2 block">Skills</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedApp.candidate.skills.map((s, i) => (
                      <Badge key={i} className="bg-blue-100 text-blue-800">{s}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Screening Results */}
              <div className="p-4 border rounded-lg bg-gradient-to-r from-purple-50 to-blue-50">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-[#111827] flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-purple-600" />
                    AI Screening
                  </h4>
                  <div className="flex items-center gap-2">
                    {selectedApp.ai_screening?.screening_status === 'completed' && (
                      <Badge className={
                        selectedApp.ai_screening.fit_level === 'excellent' ? 'bg-green-100 text-green-800' :
                        selectedApp.ai_screening.fit_level === 'good' ? 'bg-blue-100 text-blue-800' :
                        selectedApp.ai_screening.fit_level === 'fair' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }>
                        {selectedApp.ai_screening.fit_level?.toUpperCase()} - {selectedApp.ai_screening.fit_score}%
                      </Badge>
                    )}
                  </div>
                </div>

                {/* CV Preview & Re-parse buttons */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleLoadCvPreview}
                    disabled={loadingCvPreview}
                    data-testid="cv-preview-btn"
                  >
                    <FileSearch className="h-4 w-4 mr-1" />
                    {loadingCvPreview ? 'Loading...' : 'View Parsed CV'}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleRerunScreening(true)}
                    disabled={rerunningScreening}
                    data-testid="reparse-cv-btn"
                  >
                    <RefreshCw className={`h-4 w-4 mr-1 ${rerunningScreening ? 'animate-spin' : ''}`} />
                    Re-parse CV & Screen
                  </Button>
                  {selectedApp.ai_screening?.screening_status === 'completed' && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleRerunScreening(false)}
                      disabled={rerunningScreening}
                      data-testid="rerun-screening-btn"
                    >
                      <Zap className="h-4 w-4 mr-1" />
                      Re-run Screening
                    </Button>
                  )}
                  {selectedApp.ai_screening?.screening_status !== 'completed' && (
                    <Button 
                      size="sm" 
                      onClick={handleRunAIScreening}
                      disabled={runningAIScreening}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="run-ai-screening-btn"
                    >
                      {runningAIScreening ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> Analyzing...
                        </>
                      ) : (
                        <>
                          <Zap className="h-4 w-4 mr-1" /> Run AI Screening
                        </>
                      )}
                    </Button>
                  )}
                </div>
                
                {selectedApp.ai_screening?.screening_status === 'completed' ? (
                  <>
                    {/* Summary */}
                    {selectedApp.ai_screening.summary && (
                      <p className="text-[#374151] mb-3">{selectedApp.ai_screening.summary}</p>
                    )}
                    
                    {/* Recommendation */}
                    {selectedApp.ai_screening.recommendation && (
                      <div className="p-2 bg-white/50 rounded-lg mb-3">
                        <span className="text-xs text-purple-700 font-medium">Recommendation:</span>
                        <p className="text-sm text-[#374151]">{selectedApp.ai_screening.recommendation}</p>
                      </div>
                    )}
                    
                    {/* Strengths & Gaps */}
                    <div className="grid md:grid-cols-2 gap-4">
                      {selectedApp.ai_screening.strengths?.length > 0 && (
                        <div className="p-2 bg-green-50 rounded-lg">
                          <span className="text-xs text-green-700 font-medium flex items-center gap-1 mb-1">
                            <Star className="h-3 w-3" /> Strengths
                          </span>
                          <ul className="list-disc list-inside text-sm text-[#374151] space-y-1">
                            {selectedApp.ai_screening.strengths.map((s, i) => <li key={i}>{s}</li>)}
                          </ul>
                        </div>
                      )}
                      {selectedApp.ai_screening.gaps?.length > 0 && (
                        <div className="p-2 bg-amber-50 rounded-lg">
                          <span className="text-xs text-amber-700 font-medium flex items-center gap-1 mb-1">
                            <AlertCircle className="h-3 w-3" /> Gaps to Consider
                          </span>
                          <ul className="list-disc list-inside text-sm text-[#374151] space-y-1">
                            {selectedApp.ai_screening.gaps.map((g, i) => <li key={i}>{g}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                    
                    {/* Screening Notes */}
                    {selectedApp.ai_screening.screening_notes && (
                      <details className="mt-3">
                        <summary className="text-xs text-[#6B7280] cursor-pointer hover:text-[#374151]">
                          View detailed analysis notes
                        </summary>
                        <p className="text-sm text-[#6B7280] mt-2 p-2 bg-white/30 rounded">
                          {selectedApp.ai_screening.screening_notes}
                        </p>
                      </details>
                    )}
                    
                    {/* Screened At */}
                    <p className="text-xs text-[#9CA3AF] mt-3">
                      Screened {formatDate(selectedApp.ai_screened_at || selectedApp.ai_screening.screened_at)} using {selectedApp.ai_screening.model_used || 'GPT-5.2'}
                    </p>
                  </>
                ) : selectedApp.ai_screening?.screening_status === 'error' ? (
                  <div className="text-sm text-amber-700">
                    <AlertCircle className="h-4 w-4 inline mr-1" />
                    {selectedApp.ai_screening.recommendation || 'AI screening encountered an error. Please try again or review manually.'}
                  </div>
                ) : (
                  <p className="text-sm text-[#6B7280]">
                    Click "Run AI Screening" to analyze this candidate against the job requirements. 
                    The AI will provide fit scores, strengths, gaps, and recommendations to support your decision.
                  </p>
                )}
              </div>

              {/* Pipeline Stage Change */}
              <div>
                <Label className="text-[#6B7280] mb-2 block">Move to Stage</Label>
                <div className="flex flex-wrap gap-2">
                  {PIPELINE_STAGES.map((stage) => (
                    <Button
                      key={stage.key}
                      variant={selectedApp.pipeline_stage === stage.key ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleUpdateStage(stage.key)}
                      disabled={updatingStage || selectedApp.pipeline_stage === stage.key}
                      className={selectedApp.pipeline_stage === stage.key ? 'bg-[#33AADD]' : ''}
                    >
                      <span className={`h-2 w-2 rounded-full ${stage.dotColor} mr-1.5`}></span>
                      {stage.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Stage History */}
              {selectedApp.stage_history?.length > 0 && (
                <div>
                  <Label className="text-[#6B7280] mb-2 block">Stage History</Label>
                  <div className="space-y-2">
                    {selectedApp.stage_history.slice().reverse().map((entry, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <Clock className="h-3 w-3 text-[#9CA3AF]" />
                        <span className="text-[#6B7280]">{formatDate(entry.changed_at)} {formatTime(entry.changed_at)}</span>
                        <span>→</span>
                        {getStageBadge(entry.stage)}
                        {entry.changed_by && <span className="text-[#9CA3AF]">by {entry.changed_by}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* WeDo CV Generation for this Job */}
              <div className="p-3 bg-gradient-to-r from-[#33AADD]/10 to-[#33AADD]/5 rounded-lg border border-[#33AADD]/20">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-5 w-5 text-[#33AADD]" />
                  <span className="font-medium text-[#111827]">Generate WeDo CV</span>
                  <Badge variant="outline" className="text-xs">For Client</Badge>
                </div>
                <p className="text-xs text-[#6B7280] mb-3">Create professional CVs for client presentation. The CV includes job context.</p>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const url = `${API}/admin/candidates/${selectedApp.candidate_id}/generate-wedo-cv?anonymized=false&include_details=true&job_id=${selectedApp.job_id}`;
                      window.open(url, '_blank');
                      toast.success('Generating full WeDo CV...');
                    }}
                    data-testid="generate-wedo-cv-full-app"
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    Full CV
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const url = `${API}/admin/candidates/${selectedApp.candidate_id}/generate-wedo-cv?anonymized=true&include_details=true&job_id=${selectedApp.job_id}`;
                      window.open(url, '_blank');
                      toast.success('Generating anonymized WeDo CV...');
                    }}
                    data-testid="generate-wedo-cv-anon-app"
                  >
                    <User className="h-4 w-4 mr-1" />
                    Anonymized CV
                  </Button>
                </div>
              </div>

              {/* Communication Actions */}
              <div className="p-4 border rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-[#111827] flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-blue-600" />
                    Communication
                  </h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSendEmailDialogOpen(true)}
                    data-testid="send-email-btn"
                  >
                    <Mail className="h-4 w-4 mr-1" />
                    Send Email
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setScreeningNoteDialogOpen(true)}
                    data-testid="screening-note-btn"
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    Add Screening Note
                  </Button>
                  <Button
                    size="sm"
                    className="bg-[#33AADD] hover:bg-[#2890BD] text-white"
                    onClick={() => setInterviewScheduleDialogOpen(true)}
                    data-testid="schedule-interview-btn"
                  >
                    <Calendar className="h-4 w-4 mr-1" />
                    Schedule Interview
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Quick actions: Use templates for rejection, shortlist, or interview invitation
                </p>
              </div>

              {/* Interview Schedule Status */}
              {selectedApp.interview_schedule && (
                <div className="p-4 border rounded-lg bg-gradient-to-r from-indigo-50 to-purple-50">
                  <h4 className="font-semibold text-[#111827] flex items-center gap-2 mb-3">
                    <Calendar className="h-4 w-4 text-indigo-600" />
                    Interview Schedule
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Status:</span>
                      <Badge className={
                        selectedApp.interview_schedule.status === 'confirmed' 
                          ? 'bg-green-100 text-green-800' 
                          : selectedApp.interview_schedule.status === 'reschedule_requested'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-indigo-100 text-indigo-800'
                      }>
                        {selectedApp.interview_schedule.status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Type:</span>
                      <span className="font-medium capitalize">{selectedApp.interview_schedule.interview_type}</span>
                    </div>
                    {selectedApp.interview_schedule.confirmed_slot && (
                      <div className="p-2 bg-green-50 border border-green-200 rounded mt-2">
                        <p className="text-xs text-green-700 font-medium">Confirmed Time:</p>
                        <p className="text-green-800 font-semibold">
                          {selectedApp.interview_schedule.confirmed_slot.date} at {selectedApp.interview_schedule.confirmed_slot.time}
                        </p>
                      </div>
                    )}
                    {selectedApp.interview_schedule.reschedule_request && (
                      <div className="p-2 bg-amber-50 border border-amber-200 rounded mt-2">
                        <p className="text-xs text-amber-700 font-medium">Reschedule Requested:</p>
                        <p className="text-amber-800 font-semibold">
                          {selectedApp.interview_schedule.reschedule_request.preferred_date}
                          {selectedApp.interview_schedule.reschedule_request.preferred_time && 
                            ` at ${selectedApp.interview_schedule.reschedule_request.preferred_time}`}
                        </p>
                        <p className="text-xs text-amber-600">
                          Timezone: {selectedApp.interview_schedule.reschedule_request.timezone}
                        </p>
                        {selectedApp.interview_schedule.reschedule_request.note && (
                          <p className="text-xs text-amber-700 mt-1 italic">
                            "{selectedApp.interview_schedule.reschedule_request.note}"
                          </p>
                        )}
                      </div>
                    )}
                    {!selectedApp.interview_schedule.confirmed_slot && !selectedApp.interview_schedule.reschedule_request && (
                      <div className="p-2 bg-gray-50 border rounded mt-2">
                        <p className="text-xs text-gray-600">Proposed Options:</p>
                        {selectedApp.interview_schedule.proposed_slots?.map((slot, i) => (
                          <p key={i} className="text-gray-800 text-sm">
                            {slot.label || `Option ${i+1}`}: {slot.date} at {slot.time}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Communication History */}
              <CommunicationHistory
                recordType="application"
                recordId={selectedApp.application_id}
                title="Communication History"
                maxHeight="250px"
              />

              {/* Recruiter Notes */}
              <div>
                <Label className="text-[#6B7280] mb-2 block">Recruiter Notes</Label>
                <Textarea
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  rows={3}
                  placeholder="Add notes about this candidate..."
                />
                <Button variant="outline" size="sm" className="mt-2" onClick={handleUpdateNotes}>
                  Save Notes
                </Button>
              </div>

              {/* Meta */}
              <div className="text-xs text-[#9CA3AF] pt-4 border-t">
                Application ID: {selectedApp.application_id}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CV Preview Dialog */}
      <Dialog open={showCvPreview} onOpenChange={setShowCvPreview}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSearch className="h-5 w-5 text-[#33AADD]" />
              Parsed CV Preview
            </DialogTitle>
          </DialogHeader>
          {cvPreview ? (
            <div className="space-y-4">
              {/* CV Status */}
              <div className="flex items-center justify-between p-3 bg-[#F3F4F6] rounded-lg">
                <div>
                  <p className="text-sm">
                    <span className="font-medium">Word Count:</span> {cvPreview.cv_word_count || 0}
                  </p>
                  {cvPreview.cv_parsed_at && (
                    <p className="text-xs text-[#6B7280]">
                      Parsed: {formatDate(cvPreview.cv_parsed_at)}
                    </p>
                  )}
                </div>
                {cvPreview.cv_url && (
                  <a 
                    href={cvPreview.cv_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#33AADD] hover:underline text-sm flex items-center gap-1"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Original CV
                  </a>
                )}
              </div>

              {/* Parsed Content */}
              {cvPreview.cv_text_content ? (
                <div className="border rounded-lg p-4 bg-white max-h-[50vh] overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-sm text-[#374151] font-sans">
                    {cvPreview.cv_text_content}
                  </pre>
                </div>
              ) : (
                <div className="text-center py-8 text-[#6B7280]">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-[#E5E7EB]" />
                  <p>CV has not been parsed yet.</p>
                  <p className="text-sm mt-1">Click "Re-parse CV & Screen" to extract text from the CV.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <RefreshCw className="h-8 w-8 mx-auto mb-2 animate-spin text-[#33AADD]" />
              <p className="text-[#6B7280]">Loading CV preview...</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog */}
      {selectedApp && (
        <SendEmailDialog
          open={sendEmailDialogOpen}
          onOpenChange={setSendEmailDialogOpen}
          recipientName={selectedApp.candidate?.full_name || selectedApp.full_name || 'Candidate'}
          recipientEmail={selectedApp.candidate?.email || selectedApp.email || ''}
          recordType="application"
          recordId={selectedApp.application_id}
          jobTitle={selectedApp.job?.title || ''}
          onSuccess={() => {
            // Refresh data
            fetchApplications();
          }}
        />
      )}

      {/* Screening Note Dialog */}
      {selectedApp && (
        <ScreeningNoteDialog
          open={screeningNoteDialogOpen}
          onOpenChange={setScreeningNoteDialogOpen}
          applicationId={selectedApp.application_id}
          candidateName={selectedApp.candidate?.full_name || selectedApp.full_name || 'Candidate'}
          candidateEmail={selectedApp.candidate?.email || selectedApp.email || ''}
          jobTitle={selectedApp.job?.title || ''}
          onSuccess={() => {
            // Refresh data
            fetchApplications();
          }}
        />
      )}

      {/* Interview Schedule Dialog */}
      {selectedApp && (
        <InterviewScheduleDialog
          open={interviewScheduleDialogOpen}
          onOpenChange={setInterviewScheduleDialogOpen}
          applicationId={selectedApp.application_id}
          candidateName={selectedApp.candidate?.full_name || selectedApp.full_name || 'Candidate'}
          candidateEmail={selectedApp.candidate?.email || selectedApp.email || ''}
          jobTitle={selectedApp.job?.title || ''}
          onSuccess={() => {
            fetchApplications();
            // Also refresh the selected app details
            handleViewApplication(selectedApp);
          }}
        />
      )}
    </div>
  );
}

export { AdminApplicationsPage };
